/**
 * CreativeHeatmapSection.tsx
 *
 * Per-creative performance heatmap table.
 * Rows = individual ads (top 50 by opt-ins).
 * Columns = 11 metrics with per-column independent color scales.
 *
 * Color logic:
 *   - Rate columns  (CTR, Opt-in Rate, App Rate, Close Rate): blue gradient, higher = darker (better)
 *   - Cost columns  (Cost/Optin, Cost/App): amber gradient, higher = darker (worse)
 *   - Volume columns (Impressions, Clicks, Opt-ins, Apps, Closed): subtle neutral shading
 */

import React, { useState, useMemo } from "react";
import { branding } from "../../../../config/branding";
import type { IndividualCreativeFunnelRow } from "../individualCreativeBreakdown";
import { getTopNAdsData } from "../individualCreativeBreakdown";
import type { MetaLeadGenRow, MetaLeadGenFilters } from "../types";
import { lookupEocAttribution } from "../useEocAttribution";
import type { EocAttributionMap } from "../useEocAttribution";
import type { OptinAdMap } from "../useOptinAdAttribution";

// ─────────────────────────────────────────────
// TYPES
// ─────────────────────────────────────────────
type SortKey =
  | "spend" | "cpm"
  | "impressions" | "clicks" | "optins" | "applications" | "closed"
  | "ctr" | "optinRate" | "appRate" | "closeRate"
  | "costPerOptin" | "costPerApp";

type ColType = "volume" | "rate" | "cost";

interface ColDef {
  key: SortKey;
  label: string;
  type: ColType;
  format: (v: number) => string;
}

// ─────────────────────────────────────────────
// COLUMN DEFINITIONS — exact spec order
// ─────────────────────────────────────────────
const fmtCount = (v: number): string => {
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${(v / 1_000).toFixed(1)}k`;
  return String(Math.round(v));
};
const fmtPct = (v: number): string => `${v.toFixed(1)}%`;
const fmtCur = (v: number): string => `$${v.toFixed(2)}`;
const fmtSpend = (v: number): string => {
  if (v >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};

const COLUMNS: ColDef[] = [
  { key: "spend",        label: "Spend",          type: "cost",   format: fmtSpend },
  { key: "cpm",          label: "CPM",            type: "cost",   format: fmtCur   },
  { key: "impressions",  label: "Impressions",   type: "volume", format: fmtCount },
  { key: "clicks",       label: "Clicks",         type: "volume", format: fmtCount },
  { key: "optins",       label: "Opt-ins",        type: "volume", format: fmtCount },
  { key: "applications", label: "Applications",   type: "volume", format: fmtCount },
  { key: "closed",       label: "Closed",         type: "volume", format: fmtCount },
  { key: "ctr",          label: "CTR",            type: "rate",   format: fmtPct  },
  { key: "optinRate",    label: "Opt-in Rate",    type: "rate",   format: fmtPct  },
  { key: "appRate",      label: "App Rate",       type: "rate",   format: fmtPct  },
  { key: "closeRate",    label: "Close Rate",     type: "rate",   format: fmtPct  },
  { key: "costPerOptin", label: "Cost / Opt-in",  type: "cost",   format: fmtCur  },
  { key: "costPerApp",   label: "Cost / App",     type: "cost",   format: fmtCur  },
];

// ─────────────────────────────────────────────
// COLOR HELPERS
// ─────────────────────────────────────────────
function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace("#", "");
  const full = clean.length === 3
    ? clean.split("").map(c => c + c).join("")
    : clean;
  const n = parseInt(full.slice(0, 6), 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

// Precompute RGB channels from branding
const C = branding.colors;
const [rR, gR, bR] = hexToRgb(C.chart[0] ?? C.secondary); // cyan/blue — rates
const [rV, gV, bV] = hexToRgb(C.chart[1] ?? C.primary);   // indigo — volume

// Amber for cost cells (semantic: amber = caution/cost)
const [rCo, gCo, bCo]: [number, number, number] = [245, 158, 11];

function cellBg(value: number, min: number, max: number, type: ColType): string {
  const range = max - min;
  const t = range > 0 ? (value - min) / range : 0;

  if (type === "rate")   return `rgba(${rR},${gR},${bR},${(0.06 + t * 0.55).toFixed(3)})`;
  if (type === "cost")   return `rgba(${rCo},${gCo},${bCo},${(0.04 + t * 0.52).toFixed(3)})`;
  /* volume */           return `rgba(${rV},${gV},${bV},${(0.03 + t * 0.12).toFixed(3)})`;
}

// ─────────────────────────────────────────────
// PER-COLUMN RANGE COMPUTATION
// ─────────────────────────────────────────────
type Ranges = Record<SortKey, { min: number; max: number }>;

function computeRanges(data: IndividualCreativeFunnelRow[]): Ranges {
  const keys = COLUMNS.map(c => c.key);
  const result = {} as Ranges;

  for (const key of keys) {
    const vals = data.map(r => r[key] as number).filter(v => v > 0);
    result[key] = {
      min: vals.length > 0 ? Math.min(...vals) : 0,
      max: vals.length > 0 ? Math.max(...vals) : 1,
    };
  }

  return result;
}

// ─────────────────────────────────────────────
// TOOLTIP
// ─────────────────────────────────────────────
interface TooltipProps {
  ad: IndividualCreativeFunnelRow;
  x: number;
  y: number;
}

function HeatmapTooltip({ ad, x, y }: TooltipProps) {
  const items: Array<{ label: string; value: string }> = [
    { label: "Impressions",  value: fmtCount(ad.impressions) },
    { label: "Clicks",       value: fmtCount(ad.clicks) },
    { label: "Opt-ins",      value: fmtCount(ad.optins) },
    { label: "Applications", value: fmtCount(ad.applications) },
    { label: "Closed",       value: fmtCount(ad.closed) },
    { label: "CTR",          value: ad.impressions > 0 ? fmtPct(ad.ctr)          : "—" },
    { label: "Opt-in Rate",  value: ad.clicks > 0      ? fmtPct(ad.optinRate)    : "—" },
    { label: "App Rate",     value: ad.optins > 0      ? fmtPct(ad.appRate)      : "—" },
    { label: "Close Rate",   value: ad.applications > 0? fmtPct(ad.closeRate)    : "—" },
    { label: "Cost/Opt-in",  value: ad.optins > 0      ? fmtCur(ad.costPerOptin) : "—" },
    { label: "Cost/App",     value: ad.applications > 0? fmtCur(ad.costPerApp)   : "—" },
    { label: "Spend",        value: `$${ad.spend.toLocaleString("en-US", { maximumFractionDigits: 0 })}` },
  ];

  // Clamp so tooltip stays on screen
  const tipW = 220;
  const tipH = 280;
  const left = x + tipW + 16 > window.innerWidth ? x - tipW - 8 : x + 12;
  const top  = y + tipH > window.innerHeight      ? y - tipH      : y + 8;

  return (
    <div
      className="fixed z-50 pointer-events-none rounded-xl border border-border bg-popover/95 backdrop-blur-sm shadow-2xl px-3 py-3"
      style={{ left, top, width: tipW }}
    >
      <div className="flex items-center gap-2 mb-2 pb-2 border-b border-border">
        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: ad.color }} />
        <p className="text-[11px] font-bold text-foreground leading-tight truncate">{ad.shortName}</p>
      </div>
      <p className="text-[9px] font-semibold text-muted-foreground uppercase tracking-widest mb-1.5">
        {ad.typeLabel}
      </p>
      <div className="space-y-1">
        {items.map(it => (
          <div key={it.label} className="flex items-center justify-between gap-2">
            <span className="text-[10px] text-muted-foreground">{it.label}</span>
            <span className="text-[10px] font-semibold text-foreground tabular-nums">{it.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// SORT INDICATOR
// ─────────────────────────────────────────────
function SortIcon({ dir }: { dir: "asc" | "desc" | null }) {
  if (!dir) return (
    <svg width="8" height="10" viewBox="0 0 8 10" fill="none" className="opacity-25">
      <path d="M4 1V9M1 3.5L4 1L7 3.5M1 6.5L4 9L7 6.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
  return (
    <svg width="8" height="8" viewBox="0 0 8 8" fill="none" className="opacity-80">
      {dir === "desc"
        ? <path d="M4 1V7M1 4.5L4 7L7 4.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
        : <path d="M4 7V1M1 3.5L4 1L7 3.5" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
      }
    </svg>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────
interface CreativeHeatmapSectionProps {
  rows:              MetaLeadGenRow[];
  filters:           MetaLeadGenFilters;
  closeAttribution?: import("../useCloseCreativeAttribution").CloseCreativeMap;
  eocAttribution?:   EocAttributionMap;
  optinAdMap?:       OptinAdMap;
}

export function CreativeHeatmapSection({ rows, filters, closeAttribution, eocAttribution, optinAdMap }: CreativeHeatmapSectionProps) {
  const [sortKey, setSortKey]     = useState<SortKey>("applications");
  const [sortDir, setSortDir]     = useState<"asc" | "desc">("desc");
  const [tooltip, setTooltip]     = useState<TooltipProps | null>(null);

  const data = useMemo(() => getTopNAdsData(rows, filters, 50, closeAttribution, optinAdMap), [rows, filters, closeAttribution, optinAdMap]);

  const sorted = useMemo(() => {
    return [...data].sort((a, b) => {
      const av = a[sortKey] as number;
      const bv = b[sortKey] as number;
      return sortDir === "desc" ? bv - av : av - bv;
    });
  }, [data, sortKey, sortDir]);

  const ranges = useMemo(() => computeRanges(data), [data]);

  function handleHeaderClick(key: SortKey) {
    if (sortKey === key) {
      setSortDir(d => d === "desc" ? "asc" : "desc");
    } else {
      setSortKey(key);
      setSortDir("desc");
    }
  }

  function cellValue(row: IndividualCreativeFunnelRow, col: ColDef): string {
    const v = row[col.key] as number;
    if (v === 0) {
      // Zero denominator guard for derived metrics
      if (col.key === "cpm"          && row.impressions   === 0) return "—";
      if (col.key === "ctr"          && row.impressions   === 0) return "—";
      if (col.key === "optinRate"    && row.clicks         === 0) return "—";
      if (col.key === "appRate"      && row.optins         === 0) return "—";
      if (col.key === "closeRate"    && row.applications   === 0) return "—";
      if (col.key === "costPerOptin" && row.optins         === 0) return "—";
      if (col.key === "costPerApp"   && row.applications   === 0) return "—";
    }
    return col.format(v);
  }

  if (data.length === 0) return null;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-sm font-bold text-foreground">Creative Performance Heatmap</h3>
          <p className="text-xs text-muted-foreground mt-0.5">
            {sorted.length} creatives · click column headers to sort
          </p>
        </div>
        {/* Legend */}
        <div className="flex items-center gap-3 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div
              className="w-12 h-3 rounded-sm"
              style={{ background: `linear-gradient(to right, rgba(${rR},${gR},${bR},0.06), rgba(${rR},${gR},${bR},0.6))` }}
            />
            <span>Rates ↑ better</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-12 h-3 rounded-sm"
              style={{ background: `linear-gradient(to right, rgba(${rCo},${gCo},${bCo},0.04), rgba(${rCo},${gCo},${bCo},0.56))` }}
            />
            <span>Cost ↑ worse</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div
              className="w-12 h-3 rounded-sm"
              style={{ background: `linear-gradient(to right, rgba(${rV},${gV},${bV},0.03), rgba(${rV},${gV},${bV},0.15))` }}
            />
            <span>Volume</span>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="rounded-xl border border-border bg-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs border-collapse" style={{ minWidth: "1520px" }}>
            <thead>
              <tr className="border-b border-border bg-muted/30">
                {/* Sticky creative name header */}
                <th
                  className="sticky left-0 z-10 bg-muted/40 backdrop-blur-sm px-4 py-2.5 text-left text-[10px] font-semibold text-muted-foreground uppercase tracking-wider border-r border-border whitespace-nowrap"
                  style={{ minWidth: "320px" }}
                >
                  Creative
                </th>
                {COLUMNS.map(col => (
                  <th
                    key={col.key}
                    onClick={() => handleHeaderClick(col.key)}
                    className="px-3 py-2.5 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap cursor-pointer select-none hover:text-foreground transition-colors"
                  >
                    <div className="flex items-center justify-end gap-1">
                      {col.label}
                      <SortIcon dir={sortKey === col.key ? sortDir : null} />
                    </div>
                  </th>
                ))}
                {/* EOC attribution columns */}
                <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap border-l border-border/60">
                  Closes (EOC)
                </th>
                <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  Cash Collected
                </th>
                <th className="px-3 py-2.5 text-right text-[10px] font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
                  ROAS
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((row, i) => (
                <tr
                  key={row.adId}
                  className={`border-b border-border/40 transition-colors hover:bg-muted/20 ${i % 2 === 0 ? "" : "bg-muted/5"}`}
                  onMouseMove={e => setTooltip({ ad: row, x: e.clientX, y: e.clientY })}
                  onMouseLeave={() => setTooltip(null)}
                >
                  {/* Sticky creative name cell */}
                  <td
                    className="sticky left-0 z-10 bg-card border-r border-border/40 px-4 py-2"
                    style={{ minWidth: "320px" }}
                  >
                    <div className="flex items-center gap-2">
                      <span
                        className="w-2 h-2 rounded-full flex-shrink-0"
                        style={{ background: row.color }}
                      />
                      <span className="font-medium text-foreground" title={row.adName}>
                        {row.adName}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5 pl-4">
                      <p className="text-[9px] text-muted-foreground" style={{ color: row.color }}>
                        {row.typeLabel}
                      </p>
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[8px] font-bold uppercase tracking-wider bg-orange-500/15 text-orange-400 border border-orange-500/25">
                        Warm
                      </span>
                    </div>
                  </td>

                  {/* Metric cells */}
                  {COLUMNS.map(col => {
                    const v = row[col.key] as number;
                    const { min, max } = ranges[col.key];
                    const bg = v > 0 ? cellBg(v, min, max, col.type) : "transparent";
                    const display = cellValue(row, col);
                    const isActive = sortKey === col.key;

                    return (
                      <td
                        key={col.key}
                        className={`px-3 py-2 text-right tabular-nums font-medium transition-all ${
                          isActive ? "text-foreground" : "text-foreground/80"
                        }`}
                        style={{ background: bg }}
                      >
                        {display === "—"
                          ? <span className="text-muted-foreground/40">—</span>
                          : display
                        }
                      </td>
                    );
                  })}

                  {/* EOC attribution cells */}
                  {(() => {
                    const eoc = eocAttribution ? lookupEocAttribution(row.adName, eocAttribution) : null;
                    const closes = eoc?.closes ?? 0;
                    const cash   = eoc?.cashCollected ?? 0;
                    const roas   = row.spend > 0 && cash > 0 ? cash / row.spend : 0;
                    return (
                      <>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground/80 border-l border-border/60">
                          {closes > 0 ? closes : <span className="text-muted-foreground/40">—</span>}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground/80">
                          {cash > 0
                            ? <span className="text-emerald-400">${cash >= 1000 ? `${(cash / 1000).toFixed(1)}k` : cash.toFixed(0)}</span>
                            : <span className="text-muted-foreground/40">—</span>
                          }
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium text-foreground/80">
                          {roas > 0
                            ? <span className={roas >= 3 ? "text-emerald-400" : roas >= 1 ? "text-yellow-400" : "text-red-400"}>{roas.toFixed(1)}x</span>
                            : <span className="text-muted-foreground/40">—</span>
                          }
                        </td>
                      </>
                    );
                  })()}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Fixed tooltip */}
      {tooltip && <HeatmapTooltip {...tooltip} />}
    </div>
  );
}
